import { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Checkbox,
  Radio,
  RadioGroup,
} from '@nextui-org/react';
import { Tasks as TaskMainType } from 'common/src/utils/types';
import { BankAccount } from 'common/src/types/bank_accounts';
import { calculateTaskerEarnings } from 'common/src/utils/calculations';
import { getTaskAssigned } from 'common/src/api/tasks';
import { getBankAccountByTaskerId } from 'common/src/api/bank_accounts';
import { getTaskOfferById } from 'common/src/api/task_offers';

interface Asker {
  name: string;
  email: string;
}

interface TaskOffer {
  id: number;
  tasker_id: number;
  status: string;
  paid: boolean;
}

interface Tasks extends TaskMainType {
  id: number;
  asker: Asker;
  task_offers: TaskOffer[];
}

const formatCurrency = (amount: number) => {
  return amount.toLocaleString('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const TaskerEarnings = () => {
  const [tasks, setTasks] = useState<Tasks[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [filter, setFilter] = useState('all');
  const [taskEarnings, setTaskEarnings] = useState<Record<number, number>>({});

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    // Load earnings for all tasks
    const loadEarnings = async () => {
      const earnings: Record<number, number> = {};
      for (const task of tasks) {
        try {
          earnings[task.id] = await calculateTaskerEarnings(task.id);
        } catch (error) {
          console.error(`Error calculating earnings for task ${task.id}:`, error);
        }
      }
      setTaskEarnings(earnings);
    };

    if (tasks.length > 0) {
      loadEarnings();
    }
  }, [tasks]);

  const fetchTasks = async () => {
    try {
      const { data, error } = await getTaskAssigned(filter);

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowBankAccount = async (taskerId: number) => {
    try {
      const { data, error } = await getBankAccountByTaskerId(taskerId);
      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setSelectedBankAccount(data);
      onOpen();
    } catch (error) {
      console.error('Error fetching bank account:', error);
    }
  };

  const handleTaskerPaidChange = async (taskOfferId: number, paid: boolean) => {
    try {
      const { error } = await getTaskOfferById(taskOfferId, paid);
      if (error) throw error;

      setTasks(
        tasks.map((task) => ({
          ...task,
          task_offers: task.task_offers.map((offer) =>
            offer.id === taskOfferId ? { ...offer, paid } : offer
          ),
        }))
      );
    } catch (error) {
      console.error('Error updating tasker paid status:', error);
    }
  };

  const formatDate = (date: string | null, isAsap: boolean, isFlexible: boolean) => {
    if (isAsap) return 'ASAP';
    if (isFlexible) return 'Fecha flexible';
    if (!date) return 'N/A';

    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };
    return new Date(date).toLocaleDateString('es-CL', options);
  };

  return (
    <div>
      <div>
        <h2 className="text-xl font-bold">Pagos a Taskers</h2>
      </div>

      <div className="flex justify-between items-center p-4 bg-gray-50 my-4 rounded-xl">
        <RadioGroup label="Estado de pago:" value={filter} onValueChange={setFilter}>
          <Radio value="all">Ver todos</Radio>
          <Radio value="pending">Ver solo pendientes</Radio>
        </RadioGroup>
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <Table
          aria-label="Tabla de ganancias de Taskers"
          classNames={{
            base: 'max-h-[520px] overflow-y-auto',
            table: 'min-w-[800px]',
          }}
          selectionMode="none"
          isStriped
        >
          <TableHeader>
            <TableColumn>ID</TableColumn>
            <TableColumn>Título</TableColumn>
            <TableColumn>Fecha</TableColumn>
            <TableColumn>Precio (que paga asker)</TableColumn>
            <TableColumn>Precio Original</TableColumn>
            <TableColumn>Taskers</TableColumn>
          </TableHeader>
          <TableBody>
            {tasks.map((task) => (
              <TableRow key={task.id}>
                <TableCell>{task.id}</TableCell>
                <TableCell>{task.category}</TableCell>
                <TableCell>{formatDate(task.date, task.is_asap, task.is_flexible_date)}</TableCell>
                <TableCell>{formatCurrency(task.price)}</TableCell>
                <TableCell>
                  {task.original_price ? formatCurrency(task.original_price) : 'Sin Descuento'}
                </TableCell>
                <TableCell>
                  {task.task_offers.map((offer) => (
                    <div key={offer.id} className="mb-2">
                      <p>Tasker ID: {offer.tasker_id}</p>
                      <p>
                        Ganancias:{' '}
                        {taskEarnings[task.id] ? (
                          formatCurrency(taskEarnings[task.id])
                        ) : (
                          <Spinner size="sm" />
                        )}
                      </p>
                      <Button onPress={() => handleShowBankAccount(offer.tasker_id)}>
                        Ver cuenta
                      </Button>
                      <Checkbox
                        isSelected={offer.paid}
                        onValueChange={(isSelected) => handleTaskerPaidChange(offer.id, isSelected)}
                      >
                        Pagado
                      </Checkbox>
                    </div>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <ModalHeader>Información de la cuenta bancaria</ModalHeader>
          <ModalBody>
            {selectedBankAccount ? (
              <>
                <p>Nombre: {selectedBankAccount.name}</p>
                <p>Número de cuenta: {selectedBankAccount.account_number}</p>
                <p>Banco: {selectedBankAccount.bank}</p>
                <p>Tipo de cuenta: {selectedBankAccount.account_type}</p>
                <p>RUT: {selectedBankAccount.rut}</p>
              </>
            ) : (
              <p>Este Tasker aún no ha proporcionado sus datos bancarios.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onPress={onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default TaskerEarnings;
